// FINANCE FORK: global excluded-flow-categories list shared by every
// flow-derived dashboard number. Stored as a JSON-stringified string[] of
// category ids in the synced pref 'finance-excluded-flow-categories'.
import { useCallback, useMemo } from 'react';

import { useSyncedPref } from '#hooks/useSyncedPref';

// Stable identity when unset/invalid so consumers can use the array as a
// useMemo/useEffect dependency without spurious refetches.
const EMPTY: string[] = [];

// Category ids are always UUIDs. Rejecting anything else at the parse
// boundary keeps malformed pref content (the value syncs across devices)
// away from the AQL layer, whose id-typed literals are not quote-escaped.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useExcludedFlowCategories(): [
  string[],
  (next: string[]) => void,
] {
  const [raw, setRaw] = useSyncedPref('finance-excluded-flow-categories');

  const excludedCategories = useMemo(() => {
    if (!raw) return EMPTY;
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) &&
        parsed.every(id => typeof id === 'string' && UUID_RE.test(id))
        ? (parsed as string[])
        : EMPTY;
    } catch {
      return EMPTY;
    }
  }, [raw]);

  const setExcludedCategories = useCallback(
    (next: string[]) => setRaw(JSON.stringify(next)),
    [setRaw],
  );

  return [excludedCategories, setExcludedCategories];
}
